import Overlay from 'Prototype/UI/Overlay';
import Element from '../../../../Prototype/UI/Element';
import GameObject from 'GameObject/GameObject';
import Game from 'Game';
import { StartAnimation } from 'System/Animation/AnimationHelper';
import { PlaySound } from 'System/Audio/AudioHelper';
import Renderable from 'Component/Renderable';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import UIItem, { IImageData, ITextData } from 'Prototype/UI/UIItem';
import UIButton, { IUIButtonData } from 'Prototype/UI/BaseElements/UIButton';
import { TextAlignment } from 'Prototype/UI/TextAlignment';
import UITextComponent from 'Component/UI/Text';

type SeasonPassPurchaseErrorProps = {
    errorMessage: string;
};

export class SeasonPassPurchaseErrorPopup extends Element {
    private overlay: Overlay;
    protected backgroundParams: IImageData = {
        name: 'popup_background',
        layout: {
            anchors: [0, 0, 0, 0],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
            constrains: {
                heightMax: 744,
                heightMin: 744,
                widthMax: 858,
                widthMin: 858,
            },
        },
        cacheKey: 'popup_background',
    };
    protected titleParams: ITextData = {
        name: 'popup_title',
        text: 'Title',
        style: Game.style.getStyle('popup.title'),
        layout: {
            anchors: [0.5, 0.45, 0.5, 0.45],
            pivot: [0.5, 0.5],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        bounds: [460, 240],
    };
    protected titleBgParams: IImageData = {
        name: 'title_background',
        layout: {
            anchors: [0.5, 0.01, 0.5, 0.01],
            pivot: [0.5, 0],
            offsets: [0, 0, 0, 0],
            scale: [1, 1],
        },
        cacheKey: 'popup_title_bg',
    };
    protected closeBtnParams: IUIButtonData = {
        name: 'popup_close_button',
        button: {
            name: 'close_button',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                offsets: [-760, -50, -760, -50],
                scale: [1, 1],
            },
            cacheKey: 'button_close',
        },
        onClick: this.destroy.bind(this),
    };
    protected submitBtnParams: IUIButtonData = {
        name: 'popup_submit_button',
        background: {
            name: 'submit_bg',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0.5, 0],
                offsets: [-429, -739, -429, -739],
                scale: [1, 1],
            },
            cacheKey: 'blank',
        },
        text: {
            name: 'submit_txt',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 6, 0, 6],
                scale: [1, 1],
            },
            textLocalizationKey: 'Return',
            style: Game.style.getStyle('popup.red_btn'),
            align: TextAlignment.CENTER,
            bounds: [220, 130],
        },
        button: {
            name: 'submit_btn',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [-2, 50, -2, 50],
                scale: [1, 1],
            },
            cacheKey: 'button_medium_red',
        },
        buttonMode: true,
        onClick: this.destroy.bind(this),
    };

    public create(props: SeasonPassPurchaseErrorProps): void {
        super.create({
            type: 'group',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
            },
        });

        this.overlay = GameObject.Instantiate(Overlay, {
            name: 'overlay',
            preventInteraction: true,
            anchors: [0, 0, 0, 0],
            alpha: 0.9,
            color: [0, 0, 0, 225],
        });
        this.addChild(this.overlay);

        // Create background for popup
        const background: GameObject<PIXI.Container> = UIItem.createImage(this.backgroundParams);

        // Create title
        this.titleParams = {
            ...this.titleParams,
            textLocalizationKey: 'Error',
        };

        const titleBg = UIItem.createImage(this.titleBgParams);

        titleBg.addChild(UIItem.createText(this.titleParams));
        background.addChild(titleBg);

        const description: Element = GameObject.Instantiate(Element, {
            name: 'info_text',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
            },
            components: [
                {
                    type: 'text',
                    data: {
                        style: {
                            ...Game.style.getStyle('popup.text'),
                            fontSize: 150,
                            align: 'center',
                        },
                        textLocalizationKey: '',
                        bounds: [400, 120],
                    },
                },
            ],
            children: [
                {
                    name: 'error_icon',
                    layout: {
                        anchors: [1, 0.5, 1, 0.5],
                        pivot: [0, 0.45],
                        offsets: [-10, 0, -10, 0],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'popup_goal_fail',
                            },
                        },
                    ],
                },
            ],
        });
        description.getComponent(UITextComponent).text = props.errorMessage;
        background.addChild(description);

        const closeBtn = GameObject.Instantiate(UIButton, this.closeBtnParams);
        const submitBtn = GameObject.Instantiate(UIButton, this.submitBtnParams);
        background.addChild(closeBtn);
        background.addChild(submitBtn);

        const character: Element = GameObject.Instantiate(Element, {
            name: 'character',
            layout: {
                anchors: [0, 0.5, 0, 0.5],
                pivot: [0.5, 0.5],
                offsets: [40, -80, 40, -80],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: 'character_failed_1',
                    },
                },
            ],
        });
        background.addChild(character);

        this.addChild(background);

        this.show();
    }

    public show() {
        this.getComponent(Renderable).visible = false;
        this.overlay.getComponent(Renderable).alpha = 0;
        PlaySound('swoosh-1');

        StartAnimation(this, 'MenuShow');
        StartAnimation(this.overlay, 'OverlayFadeIn', { delay: 250 });
    }

    public destroy(): void {
        PlaySound('swoosh-2');
        StartAnimation(this.overlay, 'OverlayFadeOut')
        StartAnimation(this, 'MenuHide', { delay: 350 }).once(AnimationClipEvent.COMPLETE, () => {
            super.destroy();
        });
    }
}
